/**
 * Which records a workflow acts for: the routing filter editor itself, as
 * scoring rules use it, offering only the filter types that read the record
 * (`condition_types`) and the fields the trigger's record has
 * (`record_fields`), with its properties beside them.
 *
 * The same editor ([`ConditionsEditor`]) is used inside a branch, a "wait
 * until" on conditions, and a goal: each stores its conditions in the shape
 * routing filters are stored in.
 */
import { useMemo } from 'react';
import { Card, Group, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconFilter } from '@tabler/icons-react';
import { useProperties, type PropertyObject } from '../../api/properties';
import type { RecordType, WorkflowListResponse } from '../../api/workflows';
import { useVocab } from '../../vocab';
import {
  asFilters, fieldItem, mergeFieldSources, SmartFilters, syntheticNumberField, toStored, useLeadFieldSource,
  type FieldSource, type SmartFilter,
} from '../SmartFilterEditor';
import { recordWord, recordWordA } from './workflowModel';

/** Record fields that are numbers, so they get number conditions. */
const NUMBER_FIELDS = new Set([
  'score', 'revenue', 'amount_pence', 'submissions', 'emails_opened', 'emails_clicked', 'open_deals', 'won_deals', 'lost_deals',
]);

const PROPERTY_OBJECT: Record<RecordType, PropertyObject | null> = {
  submission: 'submission',
  person: 'person',
  deal: 'deal',
  task: null,
};

/** What conditions on this record type can test. */
function useConditionFields(rt: RecordType, list: WorkflowListResponse | undefined): FieldSource {
  const v = useVocab();
  const object = PROPERTY_OBJECT[rt];
  const props = useProperties(object ?? 'person', { enabled: object !== null && rt !== 'submission' }).data?.properties;
  // A submission's conditions read its custom data too, so every field seen
  // on recent leads is offered, with the described ones' labels, after its own columns.
  const leadFields = useLeadFieldSource(undefined, rt === 'submission');
  const what = recordWord(v, rt);
  return useMemo(() => {
    const own = list?.record_fields.find((r) => r.record_type === rt)?.fields ?? [];
    const ownDefs = own.map((f) => {
      const d = syntheticNumberField(f.key, f.label);
      return NUMBER_FIELDS.has(f.key) ? d : { ...d, type: 'text' as const };
    });
    const ownGroup = { group: `About the ${what}`, items: own.map((f) => fieldItem(f.key, f.label)) };
    if (rt === 'submission') {
      return mergeFieldSources({ groups: [ownGroup], defs: ownDefs }, leadFields);
    }
    const defs = object ? (props ?? []) : [];
    return {
      groups: [
        ownGroup,
        { group: v.Many('property'), items: defs.map((d) => fieldItem(d.key, d.label)) },
      ],
      defs: [...ownDefs, ...defs],
    };
  }, [list, rt, object, props, what, v, leadFields]);
}

/** Conditions stored as one list (`[{type, logic, config}]`), edited in place. */
export function ConditionsEditor({
  rt, conditions, onChange, list,
}: {
  rt: RecordType;
  conditions: unknown;
  onChange: (next: ReturnType<typeof toStored>) => void;
  list: WorkflowListResponse | undefined;
}) {
  const v = useVocab();
  const fields = useConditionFields(rt, list);
  const and = asFilters(conditions, 'and');
  const or = asFilters(conditions, 'or');
  return (
    <SmartFilters
      andFilters={and}
      orFilters={or}
      enabled
      setAndFilters={(a) => onChange(toStored(a, or))}
      setOrFilters={(o) => onChange(toStored(and, o))}
      allowTypes={list?.condition_types ?? ['field', 'geographic', 'time_based']}
      fields={fields}
      noun={recordWord(v, rt)}
    />
  );
}

export function ConditionsCard({
  rt, andFilters, orFilters, setAndFilters, setOrFilters, list,
}: {
  rt: RecordType;
  andFilters: SmartFilter[];
  orFilters: SmartFilter[];
  setAndFilters: (f: SmartFilter[]) => void;
  setOrFilters: (f: SmartFilter[]) => void;
  list: WorkflowListResponse | undefined;
}) {
  const v = useVocab();
  const fields = useConditionFields(rt, list);
  const what = recordWord(v, rt);
  const total = andFilters.length + orFilters.length;
  return (
    <Card padding="sm" withBorder data-testid="conditions-card">
      <Stack gap="sm">
        <Group gap="xs" wrap="nowrap">
          <ThemeIcon variant="light" radius="xl" size={28} color="chartViolet"><IconFilter size={15} /></ThemeIcon>
          <div>
            <Text size="sm" fw={600}>Only if</Text>
            <Text size="xs" c="dimmed">
              {total === 0
                ? `No conditions: every ${what} the trigger starts on goes on to the steps.`
                : `The steps run only for ${recordWordA(v, rt)} that matches. A condition that cannot be checked stops the run.`}
            </Text>
          </div>
        </Group>
        <SmartFilters
          andFilters={andFilters}
          orFilters={orFilters}
          enabled
          setAndFilters={setAndFilters}
          setOrFilters={setOrFilters}
          allowTypes={list?.condition_types ?? ['field', 'geographic', 'time_based']}
          fields={fields}
          noun={what}
        />
      </Stack>
    </Card>
  );
}
